import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  TextField,
  Box,
  Grid,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Paper
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button } from '@ipc/ipc-components';
import CloseIcon from '@mui/icons-material/Close';
import { Column } from 'react-table';
import { format, parseISO } from 'date-fns';
import { includes } from 'lodash';
import { useImpcCodes } from '../../hooks/useImpcCodes';

// Define types for the IMPC code API response
interface ImpcCode {
  impc?: string;
  name?: string;
  id?: number;
  exchangeOfficeName?: string;
  exchangeOfficeId?: number;
  excludeFromUnex?: boolean;
}

interface PaginatedResponse<T> {
  content: T[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  last: boolean;
  totalPages: number;
  totalElements: number;
  first: boolean;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  empty: boolean;
}

interface EditModalProps<T extends object> {
  isOpen: boolean;
  onClose: () => void;
  row: T | null;
  columns: Column<T>[];
  onSave: (editedRow: T, originalRow: T) => Promise<boolean> | boolean;
  readOnlyColumns?: string[];
  title?: string;
  requiredFields?: string[];
  textAreaColumns?: string[];
  dateColumns?: string[];
  defaultReadOnlyColumns?: string[];
  priorityReadOnlyColumns?: string[];
  excludedColumns?: string[];
  showErrorsImmediately?: boolean; // Controls whether validation errors are shown immediately during typing (true) or only after clicking Save (false)
}


const EditModal = <T extends object>({
  isOpen,
  onClose,
  row,
  columns,
  onSave,
  readOnlyColumns = [],
  excludedColumns,
  requiredFields = [],
  textAreaColumns = [],
  dateColumns = [],
  defaultReadOnlyColumns = [],
  priorityReadOnlyColumns = [],
  title = 'Edit Row',
  showErrorsImmediately = true
}: EditModalProps<T>): React.ReactElement => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));
  
  const [editedRow, setEditedRow] = useState<T | null>(null);
  const [originalRow, setOriginalRow] = useState<T | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [hasSaveBeenAttempted, setHasSaveBeenAttempted] = useState(false);

  // Fetch IMPC codes for oeFromCode and oeToCode dropdowns
  const receivingPpo = row ? (row as any).receivingPpo : null;
  const sendingPpo = row ? (row as any).sendingPpo : null;
  
  // Use our custom hook to fetch IMPC codes
  const {
    data: oeFromCodeOptions,
    isLoading: isLoadingOeFromCodes
  } = useImpcCodes({ designatedOperator: sendingPpo, enabled: isOpen && !!sendingPpo, pageSize: 500 });
  
  const {
    data: oeToCodeOptions,
    isLoading: isLoadingOeToCodes
  } = useImpcCodes({ designatedOperator: receivingPpo, enabled: isOpen && !!receivingPpo, pageSize: 500 });

  // Convert IMPC codes array to just the IMPC codes for dropdown options
  const oeFromCodeDropdownOptions = useMemo(() => {
    if (!oeFromCodeOptions?.data) return [];
    
    // Handle direct array response
    if (Array.isArray(oeFromCodeOptions.data)) {
      return oeFromCodeOptions.data
        .map((option: any) => option?.impc || '')
        .filter(Boolean);
    }
    
    return [];
  }, [oeFromCodeOptions]);

  const oeToCodeDropdownOptions = useMemo(() => {
    if (!oeToCodeOptions?.data) return [];
    
    // Handle direct array response
    if (Array.isArray(oeToCodeOptions.data)) {
      return oeToCodeOptions.data
        .map((option: any) => option?.impc || '')
        .filter(Boolean);
    }
    
    return [];
  }, [oeToCodeOptions]);

  // Combine default read-only columns with user-specified ones
  const allReadOnlyColumns = useMemo(() => {
    return [...defaultReadOnlyColumns, ...readOnlyColumns];
  }, [readOnlyColumns]);

  // Initialize edited row when modal opens with a row
  useEffect(() => {
    if (row && isOpen) {
      // Format numeric values to preserve decimal places
      const formattedRow = { ...row };
      
      // Format del_flag to display value (0 -> "No", 1 -> "Yes")
      if ((formattedRow as any).delFlag !== undefined) {
        (formattedRow as any).delFlag = (formattedRow as any).delFlag === 1 ? 'Yes' : 'No';
      }
      
      // Format kg values to preserve decimal places (e.g., 0.20 instead of 0.2)
      ['tareKg', 'netKg', 'pkg', 'gkg', 'ekg', 'grossKg', 'ibrsKg', 'ckg'].forEach(field => {
        if ((formattedRow as any)[field] !== undefined && 
            (formattedRow as any)[field] !== null && 
            (formattedRow as any)[field] !== '') {
          const value = (formattedRow as any)[field];
          const numValue = Number(value);
          
          // Only format if it's a number
          if (!isNaN(numValue)) {
            // If it's zero, format it to "0.00"
            if (numValue === 0) {
              (formattedRow as any)[field] = "0.00";
            }
            // If it has a decimal point, ensure it has 2 decimal places
            else if (value.toString().includes('.')) {
              const [whole, decimal] = value.toString().split('.');
              // If it has 1 decimal place, format to 2
              if (decimal.length === 1) {
                (formattedRow as any)[field] = `${whole}.${decimal}0`;
              }
            }
            // If it's a whole number, add ".00"
            else if (Number.isInteger(numValue)) {
              (formattedRow as any)[field] = `${value}.00`;
            }
          }
        }
      });
      
      // Format labelWeight to have 1 decimal place (e.g., 0.2 instead of 0.20)
      if ((formattedRow as any).labelWeight !== undefined && 
          (formattedRow as any).labelWeight !== null && 
          (formattedRow as any).labelWeight !== '') {
        const value = (formattedRow as any).labelWeight;
        const numValue = Number(value);
        
        // Only format if it's a number
        if (!isNaN(numValue)) {
          // Always format to exactly 1 decimal place using toFixed
          (formattedRow as any).labelWeight = numValue.toFixed(1);
        }
      }
      
      // Do not format receivedWeightStratum - just validate it later
      
      // Format date fields to YYYYMMDD format if they are in ISO format
      ['samplingDate', 'despatchDate'].forEach(field => {
        if ((formattedRow as any)[field] && 
            typeof (formattedRow as any)[field] === 'string' && 
            (formattedRow as any)[field].includes('-')) {
          try {
            // Parse ISO date and reformat to YYYYMMDD
            const isoDate = parseISO((formattedRow as any)[field]);
            if (!isNaN(isoDate.getTime())) {
              (formattedRow as any)[field] = format(isoDate, 'yyyyMMdd');
            }
          } catch (error) {
            // If parsing fails, keep the original value
            console.error(`Error formatting date field ${field}:`, error);
          }
        }
      });
      
      // Format subsample_rate to always have 4 decimal places
      if ((formattedRow as any).subsampleRate !== undefined && 
          (formattedRow as any).subsampleRate !== null && 
          (formattedRow as any).subsampleRate !== '') {
        const value = (formattedRow as any).subsampleRate;
        // Only format if it's a number
        if (!isNaN(Number(value))) {
          // Ensure it has 4 decimal places
          const strValue = value.toString();
          if (strValue.includes('.')) {
            const [whole, decimal] = strValue.split('.');
            // Pad with zeros to get 4 decimal places
            const paddedDecimal = decimal.padEnd(4, '0').slice(0, 4);
            (formattedRow as any).subsampleRate = `${whole}.${paddedDecimal}`;
          } else {
            // If no decimal part, add .0000
            (formattedRow as any).subsampleRate = `${strValue}.0000`;
          }
        }
      }
      
      setEditedRow(formattedRow);
      setOriginalRow(row);
      
      // Initialize validation errors map for tracking only, but don't display them initially
      const initialErrors: Record<string, string> = {};
      for (const field of requiredFields) {
        if (field in formattedRow) {
          const value = (formattedRow as any)[field];
          if (value === null || value === undefined || value === '') {
            initialErrors[field] = 'Required';
          }
        }
      }
      setValidationErrors(initialErrors);
    }
  }, [row, isOpen]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setEditedRow(null);
      setOriginalRow(null);
      setIsSaving(false);
      setValidationErrors({});
      setHasSaveBeenAttempted(false);
    }
  }, [isOpen]);

  // Handle cell value change
  const handleCellChange = (column: string, value: any) => {
    if (!editedRow) return;
    
    // Skip excluded columns
    if (excludedColumns?.includes(column)) {
      return;
    }
    
    const errors = { ...validationErrors };
    
    // Check if this is a required field
    if (requiredFields.includes(column)) {
      if (value === null || value === undefined || value === '') {
        errors[column] = 'Required';
      } else {
        delete errors[column];
      }
    }
    
    // Validation for integer fields
    if (column === 'receptacleNumber') {
      // Convert to number for validation
      const numValue = Number(value);
      
      // Check if it's a valid integer and <= 999
      if (value !== '' && (!Number.isInteger(numValue) || numValue > 999 || numValue < 0)) {
        errors[column] = 'Invalid';
      } else {
        delete errors[column];
      }
    }
    
    // Validation for despatchNumber
    else if (column === 'despatchNumber') {
      // Convert to number for validation
      const numValue = Number(value);
      
      // Check if it's a valid integer and <= 9999
      if (value !== '' && (!Number.isInteger(numValue) || numValue > 9999 || numValue < 0)) {
        errors[column] = 'Invalid';
      } else {
        delete errors[column];
      }
    }
    
    // Validation for kg fields to have 2-3 decimal places
    else if (['tareKg', 'netKg', 'pkg', 'gkg', 'ekg', 'grossKg',].includes(column)) {
      // Convert to number for validation
      const numValue = Number(value);
      
      if (value !== '' && isNaN(numValue)) {
        errors[column] = 'Invalid';
      } else if (value !== '' && numValue !== 0) {
        // Only check decimal places for non-zero values
        // Zero is valid in any format (0, 0.0, 0.00)
        const decimalStr = value.toString().split('.')[1] || '';
        if (decimalStr.length !== 2) {
          errors[column] = 'Invalid';
        } else {
          delete errors[column];
        }
      } else {
        delete errors[column];
      }
    }
    
    // Validation for labelWeight to have 1 decimal place
    else if (column === 'labelWeight') {
      // Convert to number for validation
      const numValue = Number(value);
      
      if (value !== '' && isNaN(numValue)) {
        errors[column] = 'Invalid';
      } else if (value !== '' && numValue !== 0) {
        // Only check decimal places for non-zero values
        const decimalStr = value.toString().split('.')[1] || '';
        if (decimalStr.length !== 1) {
          errors[column] = 'Must have exactly 1 decimal place';
        } else {
          delete errors[column];
        }
      } else {
        delete errors[column];
      }
    }
    
    // Validation for receivedWeightStratum
    else if (column === 'receivedWeightStratum' && !excludedColumns?.includes(column)) {
      // Convert to number for validation
      const numValue = Number(value);
      
      if (value !== '' && isNaN(numValue)) {
        errors[column] = 'Invalid number format';
      } else if (value !== '') {
        // Check if the number has at least 1 decimal place
        const valueAsString = value.toString();
        if (valueAsString.includes('.')) {
          const [_, decimal] = valueAsString.split('.');
          if (decimal.length < 1) {
            errors[column] = 'Must have at least 1 decimal place';
          } else {
            delete errors[column];
          }
        } else {
          // No decimal point
          errors[column] = 'Must have at least 1 decimal place';
        }
      } else {
        delete errors[column];
      }
    }
    
    // Validation for subsample_rate - must have 4 decimal places and be >= 1.0000
    else if (column === 'subsampleRate') {
      // Convert to number for validation
      const numValue = Number(value);
      
      // Allow any input while typing, only validate on blur or save
      if (value !== '' && isNaN(numValue)) {
        errors[column] = 'Invalid number format';
      } else if (value !== '' && numValue < 1) {
        errors[column] = 'Must be at least 1.0000';
      } else if (value !== '' && !isNaN(numValue)) {
        // Check for 4 decimal places
        const decimalStr = value.toString().split('.')[1] || '';
        if (decimalStr.length !== 4) {
          errors[column] = 'Must have exactly 4 decimal places';
        } else {
          delete errors[column];
        }
      } else {
        delete errors[column];
      }
      
      // Don't auto-format while typing - just store the raw value
      // This allows users to freely edit the field
      const updatedRow = {
        ...editedRow,
        [column]: value
      };
      
      setEditedRow(updatedRow);
      setValidationErrors(errors);
      return;
    }
    
    // Create a new copy of the edited row with the updated value
    const updatedRow = {
      ...editedRow,
      [column]: value
    };
    
    setEditedRow(updatedRow);
    setValidationErrors(errors);
  };

  // Handle date value change
  const handleDateChange = (column: string, date: Date | null) => {
    if (!editedRow) return;
    
    // Skip excluded columns
    if (excludedColumns?.includes(column)) {
      return;
    }
    
    const errors = { ...validationErrors };
    
    // Format date as YYYYMMDD string (without hyphens) or null if date is null
    const formattedDate = date ? format(date, 'yyyyMMdd') : null;
    
    // Check if this is a required field
    if (requiredFields.includes(column)) {
      if (formattedDate === null) {
        errors[column] = 'Required';
      } else {
        delete errors[column];
      }
    } else {
      // For non-required fields, always remove any errors when a date is selected or cleared
      delete errors[column];
    }
    
    // Create a new copy of the edited row with the updated value
    const updatedRow = {
      ...editedRow,
      [column]: formattedDate
    };
    
    setEditedRow(updatedRow);
    
    // Always update the validation errors
    setValidationErrors(errors);
  };

  // Handle save button click
  const handleSave = async () => {
    if (!editedRow || !originalRow) {
      return;
    }
    
    // Set save has been attempted
    setHasSaveBeenAttempted(true);
    
    // Final validation before saving
    const errors: Record<string, string> = {};
    
    // Check for required fields that are empty
    for (const field of requiredFields) {
      if (field in editedRow) {
        const value = (editedRow as any)[field];
        if (value === null || value === undefined || value === '') {
          errors[field] = 'Required';
        }
      }
    }
    
    // Convert delFlag from display value to numeric value
    if (typeof (editedRow as any).delFlag !== 'undefined') {
      (editedRow as any).delFlag = (editedRow as any).delFlag === 'Yes' ? 1 : 0;
    }
    
    // Validate receptacleNumber
    if (typeof (editedRow as any).receptacleNumber !== 'undefined') {
      const receptacleNumber = Number((editedRow as any).receptacleNumber);
      if ((editedRow as any).receptacleNumber !== '' && 
          (!Number.isInteger(receptacleNumber) || receptacleNumber > 999 || receptacleNumber < 0)) {
        errors.receptacleNumber = 'Invalid';
      }
    }
    
    // Validate despatchNumber
    if (typeof (editedRow as any).despatchNumber !== 'undefined') {
      const despatchNumber = Number((editedRow as any).despatchNumber);
      if ((editedRow as any).despatchNumber !== '' && 
          (!Number.isInteger(despatchNumber) || despatchNumber > 9999 || despatchNumber < 0)) {
        errors.despatchNumber = 'Invalid';
      }
    }
    
    // Validate date fields (samplingDate, despatchDate)
    // The dates are stored in YYYYMMDD format (e.g., "20240122") - no additional validation needed
    
    // Validate kg fields
    const kgFields = ['tareKg', 'netKg', 'pkg', 'gkg', 'ekg', 'grossKg', 'ibrsKg', 'ckg',];
    kgFields.forEach(field => {
      if (typeof (editedRow as any)[field] !== 'undefined' && (editedRow as any)[field] !== '') {
        const value = (editedRow as any)[field];
        const numValue = Number(value);
        
        if (isNaN(numValue)) {
          errors[field] = 'Invalid';
        } else if (numValue !== 0) {
          // Only check decimal places for non-zero values
          // Zero is valid in any format (0, 0.0, 0.00)
          const decimalStr = value.toString().split('.')[1] || '';
          if (decimalStr.length !== 2 && decimalStr.length !== 3) {
            errors[field] = 'Invalid';
          }
        }
      }
    });
    
    // Validate labelWeight separately
    if (typeof (editedRow as any).labelWeight !== 'undefined' && (editedRow as any).labelWeight !== '') {
      const value = (editedRow as any).labelWeight;
      const numValue = Number(value);
      
      if (isNaN(numValue)) {
        errors.labelWeight = 'Invalid';
      } else if (numValue !== 0) {
        // Check decimal places for non-zero values
        const decimalStr = value.toString().split('.')[1] || '';
        if (decimalStr.length !== 1) {
          // If it's not exactly 1 decimal place, format it
          const formattedValue = numValue.toFixed(1);
          (editedRow as any).labelWeight = formattedValue;
        }
      } else {
        (editedRow as any).labelWeight = "";
      }
    }
    
    // Validate receivedWeightStratum
    if (typeof (editedRow as any).receivedWeightStratum !== 'undefined' && (editedRow as any).receivedWeightStratum !== '' && !excludedColumns?.includes('receivedWeightStratum')) {
      const value = (editedRow as any).receivedWeightStratum;
      const numValue = Number(value);
      
      if (isNaN(numValue)) {
        errors.receivedWeightStratum = 'Invalid';
      } else {
        // Check if the number has at least 1 decimal place
        const valueAsString = value.toString();
        if (valueAsString.includes('.')) {
          const [_, decimal] = valueAsString.split('.');
          if (decimal.length < 1) {
            errors.receivedWeightStratum = 'Must have at least 1 decimal place';
          } else {
            delete errors.receivedWeightStratum;
          }
        } else {
          // No decimal point
          errors.receivedWeightStratum = 'Must have at least 1 decimal place';
        }
      }
    }
    
    // Validate subsample_rate
    if (typeof (editedRow as any).subsampleRate !== 'undefined' && (editedRow as any).subsampleRate !== '') {
      const value = (editedRow as any).subsampleRate;
      const numValue = Number(value);
      
      if (isNaN(numValue) || numValue < 1) {
        errors.subsampleRate = 'Invalid';
      } else {
        // Format the value to ensure it has 4 decimal places before saving
        let formattedValue = value;
        if (typeof value === 'string' && value.includes('.')) {
          const [whole, decimal] = value.split('.');
          // Pad with zeros to get 4 decimal places
          const paddedDecimal = decimal.padEnd(4, '0').slice(0, 4);
          formattedValue = `${whole}.${paddedDecimal}`;
        } else if (typeof value === 'number') {
          formattedValue = value.toFixed(4);
        } else {
          // If no decimal part, add .0000
          formattedValue = `${value}.0000`;
        }
        
        // Update the value in editedRow
        (editedRow as any).subsampleRate = formattedValue;
      }
    }
    
    // If there are validation errors, don't submit
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    try {
      setIsSaving(true);
      const result = await Promise.resolve(onSave(editedRow, originalRow));
      if (result) {
        // If save was successful, close the modal
        onClose();
      }
    } catch (error) {
      console.error('Error saving row:', error);
      // Mark fields as invalid without showing specific messages
      setValidationErrors({ 
        _global: '' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel button click
  const handleCancel = () => {
    onClose();
  };

  // Get all fields, ordered by priority (readonly first, then the rest)
  const getOrderedColumns = () => {
    if (!columns) return [] as Column<T>[];
    
    // First get the priority read-only columns in their specified order
    const priorityColumns = columns.filter(col => 
      typeof col.accessor === 'string' && 
      priorityReadOnlyColumns.includes(col.accessor) &&
      !excludedColumns?.includes(col.accessor)
    ).sort((a, b) => {
      const aAccessor = a.accessor as string;
      const bAccessor = b.accessor as string;
      return priorityReadOnlyColumns.indexOf(aAccessor) - 
             priorityReadOnlyColumns.indexOf(bAccessor);
    });
    
    // Then get the comment column
    const commentColumn = columns.find(col => 
      typeof col.accessor === 'string' && 
      textAreaColumns.includes(col.accessor) &&
      !excludedColumns?.includes(col.accessor)
    );
    
    // Then get all other columns (excluding priority ones, comments, 'actions', and excluded columns)
    const otherColumns = columns.filter(col => 
      typeof col.accessor === 'string' && 
      !priorityReadOnlyColumns.includes(col.accessor) &&
      !textAreaColumns.includes(col.accessor) &&
      !excludedColumns?.includes(col.accessor) &&
      col.accessor !== 'actions'
    );
    
    // Return all columns in the desired order
    return [
      ...priorityColumns,
      ...(commentColumn ? [commentColumn] : []),
      ...otherColumns
    ];
  };

  // Render field based on column type
  const renderField = (column: Column<T>) => {
    if (!editedRow) return null;
    
    const accessor = column.accessor as string;
    
    // Skip excluded columns
    if (excludedColumns?.includes(accessor) || 
        excludedColumns?.includes(column.Header?.toString().toLowerCase() || '')) {
      return null;
    }
    
    const columnHeader = column.Header?.toString().toLowerCase() || '';
    const isReadOnly = allReadOnlyColumns.includes(accessor) ||
                      priorityReadOnlyColumns.includes(accessor);
    const isTextArea = textAreaColumns.includes(accessor);
    const isDateField = dateColumns.includes(accessor) || 
                        columnHeader.includes('sampling_date') || 
                        columnHeader.includes('despatch_date');
    const value = (editedRow as any)[accessor] !== undefined ? (editedRow as any)[accessor] : '';
    
    // Check if field is required and empty
    const isRequiredAndEmpty = requiredFields.includes(accessor) && 
                              (value === null || value === undefined || value === '');

    // Show validation errors based on the showErrorsImmediately flag or if save was attempted
    // For date fields, we need to handle validation differently
    const hasError = isDateField
      ? (showErrorsImmediately || hasSaveBeenAttempted) && !!validationErrors[accessor]
      : (showErrorsImmediately || hasSaveBeenAttempted) && (!!validationErrors[accessor] || isRequiredAndEmpty);
    
    // Special handling for barcode field - will be rendered with different styling
    const isBarcode = accessor === 'barcode' || columnHeader === 'barcode';
    
    // Check if field is required to add asterisk to label
    const isRequired = requiredFields.includes(accessor);
    const fieldLabel = isRequired ? `${column.Header} *` : column.Header;
    
    // Generate data-testid for the field
    const fieldTestId = `edit-modal-field-${accessor}`;

    // Special handling for 'todo' column with star icon
    if (accessor === 'todo') {
      return (
        <TextField
          label={fieldLabel}
          value="This field is handled automatically"
          disabled
          fullWidth
          margin="dense"
          size="small"
          data-testid={fieldTestId}
        />
      );
    }

    // Special handling for receptacleNumber - numeric input with validation
    if (accessor === 'receptacleNumber') {
      return (
        <TextField
          label={fieldLabel}
          value={value}
          onChange={(e) => handleCellChange(accessor, e.target.value)}
          disabled={isReadOnly}
          error={hasError}
          helperText=""
          fullWidth
          margin="dense"
          size="small"
          type="number"
          inputProps={{ 
            min: 0, 
            max: 999,
            step: 1
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            mb: 0.5,
            bgcolor: isReadOnly ? '#f5f5f5' : 'white',
            '& .MuiOutlinedInput-root': {
              bgcolor: isReadOnly ? '#f5f5f5' : 'white'
            }
          }}
          data-testid={fieldTestId}
        />
      );
    }

    // Special handling for despatchNumber - numeric input with validation
    if (accessor === 'despatchNumber') {
      return (
        <TextField
          label={fieldLabel}
          value={value}
          onChange={(e) => handleCellChange(accessor, e.target.value)}
          disabled={isReadOnly}
          error={hasError}
          helperText=""
          fullWidth
          margin="dense"
          size="small"
          type="number"
          inputProps={{ 
            min: 0, 
            max: 9999,
            step: 1
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            mb: 0.5,
            bgcolor: isReadOnly ? '#f5f5f5' : 'white',
            '& .MuiOutlinedInput-root': {
              bgcolor: isReadOnly ? '#f5f5f5' : 'white'
            }
          }}
          data-testid={fieldTestId}
        />
      );
    }

    // Special handling for subsample_rate - numeric input with validation
    if (accessor === 'subsampleRate') {
      return (
        <TextField
          label={fieldLabel}
          value={value}
          onChange={(e) => handleCellChange(accessor, e.target.value)}
          onBlur={() => {
            // Format on blur if it's a valid number >= 1
            if (editedRow) {
              const currentValue = (editedRow as any)[accessor];
              const numValue = Number(currentValue);
              
              if (currentValue !== '' && !isNaN(numValue) && numValue >= 1) {
                // Format to 4 decimal places
                let formattedValue: string;
                if (currentValue.toString().includes('.')) {
                  const [whole, decimal] = currentValue.toString().split('.');
                  // Pad with zeros to get 4 decimal places
                  const paddedDecimal = decimal.padEnd(4, '0').slice(0, 4);
                  formattedValue = `${whole}.${paddedDecimal}`;
                } else {
                  // If no decimal part, add .0000
                  formattedValue = `${currentValue}.0000`;
                }
                
                // Update the edited row
                const updatedRow = {
                  ...editedRow,
                  [accessor]: formattedValue
                };
                
                setEditedRow(updatedRow);
              }
            }
          }}
          disabled={isReadOnly}
          error={hasError}
          fullWidth
          margin="dense"
          size="small"
          type="text" // Changed from number to text for more flexible editing
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            mb: 0.5,
            bgcolor: isReadOnly ? '#f5f5f5' : 'white',
            '& .MuiOutlinedInput-root': {
              bgcolor: isReadOnly ? '#f5f5f5' : 'white'
            }
          }}
          data-testid={fieldTestId}
        />
      );
    }

    // Special handling for kg fields - numeric input with decimal validation
    if (['tareKg', 'netKg', 'pkg', 'gkg', 'ekg'].includes(accessor)) {
      return (
        <TextField
          label={fieldLabel}
          value={value}
          onChange={(e) => handleCellChange(accessor, e.target.value)}
          disabled={isReadOnly}
          error={hasError}
          helperText=""
          fullWidth
          margin="dense"
          size="small"
          type="number"
          inputProps={{ 
            step: 0.01
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            mb: 0.5,
            bgcolor: isReadOnly ? '#f5f5f5' : 'white',
            '& .MuiOutlinedInput-root': {
              bgcolor: isReadOnly ? '#f5f5f5' : 'white'
            }
          }}
          data-testid={fieldTestId}
        />
      );
    }
    
    // Special handling for labelWeight - numeric input with 1 decimal place
    if (accessor === 'labelWeight') {
      return (
        <TextField
          label={fieldLabel}
          value={value}
          onChange={(e) => handleCellChange(accessor, e.target.value)}
          onBlur={() => {
            // Format on blur
            if (editedRow) {
              const currentValue = (editedRow as any)[accessor];
              const numValue = Number(currentValue);
              
              if (currentValue !== '' && !isNaN(numValue)) {
                // Format to 1 decimal place
                const formattedValue = numValue.toFixed(1);
                
                // Update the edited row
                const updatedRow = {
                  ...editedRow,
                  [accessor]: formattedValue
                };
                
                setEditedRow(updatedRow);
              }
            }
          }}
          disabled={isReadOnly}
          error={hasError}
          fullWidth
          margin="dense"
          size="small"
          type="text"
          inputProps={{ 
            inputMode: "decimal"
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            mb: 0.5,
            bgcolor: isReadOnly ? '#f5f5f5' : 'white',
            '& .MuiOutlinedInput-root': {
              bgcolor: isReadOnly ? '#f5f5f5' : 'white'
            }
          }}
          data-testid={fieldTestId}
        />
      );
    }
    
    // Handle dropdown fields
    const dropdownFields: Record<string, string[]> = {
      'mailCategory': ['A', 'B', 'C', 'D'],
      'formatCorrected': ['P', 'G', 'E', 'X', 'J', 'Z','C'],
      'formatAnnounced': ['P', 'G', 'E', 'X', 'J', 'Z','C', 'N'],
      'samplingMethod': ['continuous', 'continuous pr', 'continuous rc', 'continuous co', 'continuous sm'],
      'netOrGrossAccounting': ['N', 'G'],
      'oeFromCode': isLoadingOeFromCodes 
        ? ['Loading...'] 
        : oeFromCodeDropdownOptions,
      'oeToCode': isLoadingOeToCodes 
        ? ['Loading...'] 
        : oeToCodeDropdownOptions,
      'receptacleMailSubclass': ['UA', 'UE', 'UL', 'UN', 'UP', 'UY', 'UZ'],
      'receptacleType': ['BG', 'CG', 'CN', 'FW', 'GU', 'IB', 'IL', 'IS', 'PB', 'PU', 'PX'],
      'despatchMailSubclass': ['UA', 'UE', 'UL', 'UN', 'UP', 'UY', 'UZ'],
      'delFlag': ['No', 'Yes'],
      'receptacleCategory': ['tray', 'bag', 'pallet', 'container', 'dummy_full_count_bag', 'dummy_full_count_pallet', 'dummy_full_count_container', 'dummy_full_count_tray']
    };
    
    // Check if the current field should be a dropdown
    const isDropdown = accessor in dropdownFields || 
                       columnHeader.includes('mail_category') || 
                       columnHeader.includes('format_corrected') || 
                       columnHeader.includes('format_announced') || 
                       columnHeader.includes('sampling_method') || 
                       columnHeader.includes('net_or_gross_accounting') || 
                       columnHeader.includes('oe_from_code') || 
                       columnHeader.includes('oe_to_code') || 
                       columnHeader.includes('receptacle_mail_subclass') || 
                       columnHeader.includes('receptacle_type') ||
                       columnHeader.includes('despatch_mail_subclass') ||
                       accessor === 'delFlag' || 
                       columnHeader.includes('del_flag') ||
                       columnHeader.includes('receptacle_category');

    // Styling for priority read-only fields (sample_id, version, changeBy)
    const isPriorityField = priorityReadOnlyColumns.includes(accessor);
    
    // For textarea fields (comments)
    if (isTextArea) {
      return (
        <TextField
          label={fieldLabel}
          value={value}
          onChange={(e) => handleCellChange(accessor, e.target.value)}
          disabled={isReadOnly}
          error={hasError}
          helperText=""
          fullWidth
          margin="dense"
          size="small"
          multiline
          rows={3}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            mb: 1,
            bgcolor: isReadOnly ? '#f5f5f5' : 'white',
            '& .MuiOutlinedInput-root': {
              bgcolor: isReadOnly ? '#f5f5f5' : 'white'
            }
          }}
          data-testid={fieldTestId}
        />
      );
    }
    
    // For dropdown fields
    if (isDropdown) {
      // Special handling for delFlag
      if (accessor === 'delFlag' || columnHeader.includes('del_flag')) {
        return (
          <FormControl 
            fullWidth 
            margin="dense" 
            size="small"
            error={hasError}
            disabled={isReadOnly}
            variant="outlined"
            sx={{ 
              minWidth: '100%',
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
              },
              mb: 0.5,
              '& .MuiOutlinedInput-root': {
                bgcolor: isReadOnly ? '#f5f5f5' : 'white'
              }
            }}
            data-testid={fieldTestId}
          >
            <InputLabel id={`${accessor}-label`}>{fieldLabel}</InputLabel>
            <Select
              labelId={`${accessor}-label`}
              value={value || 'No'} // Default to 'No' if empty
              onChange={(e) => handleCellChange(accessor, e.target.value)}
              label={fieldLabel}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 224,
                    width: 'auto',
                    marginTop: 8
                  },
                },
              }}
              data-testid={`${fieldTestId}-select`}
            >
              <MenuItem value="No" data-testid={`${fieldTestId}-option-no`}>No</MenuItem>
              <MenuItem value="Yes" data-testid={`${fieldTestId}-option-yes`}>Yes</MenuItem>
            </Select>
          </FormControl>
        );
      }
      
      // Get options for this dropdown, defaulting to the standard options
      const defaultOptions = ['UA', 'UE', 'UL', 'UN', 'UP', 'UY', 'UZ'];
      let options = typeof accessor === 'string' && accessor in dropdownFields 
        ? dropdownFields[accessor] 
        : defaultOptions;
      
      // For oeFromCode or oeToCode, ensure the current value is in the options list
      if ((accessor === 'oeFromCode' || accessor === 'oeToCode' || 
           columnHeader.includes('oe_from_code') || columnHeader.includes('oe_to_code')) && 
          value && !options.includes(value) && !isLoadingOeFromCodes && !isLoadingOeToCodes) {
        // Add the current value to the options if it's not already there
        options = [...options, value];
      }
      
      // Show loading indicator in the select for IMPC codes
      const isLoading = (accessor === 'oeFromCode' && isLoadingOeFromCodes) || 
                       (accessor === 'oeToCode' && isLoadingOeToCodes);
      
      return (
        <FormControl 
          fullWidth 
          margin="dense" 
          size="small"
          error={hasError}
          disabled={isReadOnly || isLoading}
          variant="outlined"
          sx={{ 
            minWidth: '100%',
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            mb: 0.5,
            '& .MuiOutlinedInput-root': {
              bgcolor: isReadOnly ? '#f5f5f5' : 'white'
            }
          }}
          data-testid={fieldTestId}
        >
          <InputLabel id={`${accessor}-label`}>{fieldLabel}</InputLabel>
          <Select
            labelId={`${accessor}-label`}
            value={value || ''}
            onChange={(e) => handleCellChange(accessor, e.target.value)}
            label={fieldLabel}
            startAdornment={isLoading ? (
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            ) : null}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 224,
                  width: 'auto',
                  marginTop: 8
                },
              },
            }}
            data-testid={`${fieldTestId}-select`}
          >
            {(accessor === 'receptacleMailSubclass' || accessor === 'formatAnnounced' || accessor === 'receptacleType' || accessor === 'receptacleCategory') && 
              <MenuItem value="" data-testid={`${fieldTestId}-option-none`}><em>None</em></MenuItem>}
            {options.map((option: string) => (
              <MenuItem key={option} value={option} data-testid={`${fieldTestId}-option-${option}`}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    
    // For priority read-only fields (sample_id, version, changeBy)
    if (isPriorityField) {
      return (
        <TextField
          label={fieldLabel}
          value={value}
          disabled={true}
          error={hasError}
          helperText=""
          fullWidth
          margin="dense"
          size="small"
          sx={{
            '& .MuiInputBase-input': {
              fontWeight: 'bold',
              fontSize: '0.9rem',
            },
            mb: 1,
            bgcolor: '#f5f5f5',
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f5f5f5'
            }
          }}
          data-testid={fieldTestId}
        />
      );
    }
    
    // For date fields (sampling_date, despatch_date)
    if (isDateField) {
      // Parse the date string to a Date object for the DatePicker
      let dateValue = null;
      if (value && typeof value === 'string') {
        try {
          // Handle both formats: YYYYMMDD and YYYY-MM-DD (ISO)
          if (value.includes('-')) {
            // ISO format (YYYY-MM-DD)
            dateValue = parseISO(value);
          } else {
            // YYYYMMDD format
            const year = parseInt(value.substring(0, 4));
            const month = parseInt(value.substring(4, 6)) - 1; // months are 0-indexed in JS Date
            const day = parseInt(value.substring(6, 8));
            dateValue = new Date(year, month, day);
          }
          
          // Check if valid date
          if (isNaN(dateValue.getTime())) {
            dateValue = null;
          }
        } catch (error) {
          dateValue = null;
        }
      }
      
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={fieldLabel}
            value={dateValue}
            onChange={(newDate) => handleDateChange(accessor, newDate)}
            disabled={isReadOnly}
            format="dd/MM/yyyy"
            slotProps={{
              textField: {
                fullWidth: true,
                margin: "dense",
                size: "small",
                error: hasError,
                helperText:  "",
                sx: {
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                  },
                  mb: 0.5,
                  bgcolor: isReadOnly ? '#f5f5f5' : 'white',
                  '& .MuiOutlinedInput-root': {
                    bgcolor: isReadOnly ? '#f5f5f5' : 'white'
                  }
                }
              },
              day: {
                sx: {
                  fontSize: '0.875rem'
                }
              }
            }}
          />
        </LocalizationProvider>
      );
    }
    
    // For receivedWeightStratum - numeric input with at least 1 decimal place
    if (accessor === 'receivedWeightStratum') {
      return (
        <TextField
          label={fieldLabel}
          value={value}
          onChange={(e) => handleCellChange(accessor, e.target.value)}
          disabled={isReadOnly}
          error={hasError}
          helperText={hasError ? "Must have at least 1 decimal place" : ""}
          fullWidth
          margin="dense"
          size="small"
          type="number"
          inputProps={{ 
            step: 0.1
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            mb: 0.5,
            bgcolor: isReadOnly ? '#f5f5f5' : 'white',
            '& .MuiOutlinedInput-root': {
              bgcolor: isReadOnly ? '#f5f5f5' : 'white'
            }
          }}
          data-testid={fieldTestId}
        />
      );
    }
    
    // For regular text fields
    return (
      <TextField
        label={fieldLabel}
        value={value}
        onChange={(e) => handleCellChange(accessor, e.target.value)}
        disabled={isReadOnly}
        error={hasError}
        helperText=""
        fullWidth
        margin="dense"
        size="small"
        sx={{
          '& .MuiInputBase-input': {
            fontSize: '0.875rem',
          },
          mb: 0.5,
          bgcolor: isReadOnly ? '#f5f5f5' : 'white',
          '& .MuiOutlinedInput-root': {
            bgcolor: isReadOnly ? '#f5f5f5' : 'white'
          }
        }}
        data-testid={fieldTestId}
      />
    );
  };

  const orderedColumns = getOrderedColumns();

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      data-testid="edit-modal"
    >
      {/* Header */}
      <DialogTitle
        sx={{
          m: 0,
          p: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}
        data-testid="edit-modal-title"
      >
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary',
          }}
          data-testid="edit-modal-close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent 
        dividers 
        sx={{ 
          p: 2, 
          overflow: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
        data-testid="edit-modal-content"
      >
        {validationErrors._global && (
          <Typography color="error" variant="body2" sx={{ mb: 1.5 }} data-testid="edit-modal-global-error">
            {validationErrors._global}
          </Typography>
        )}
        
        {/* Priority Fields Section */}
        {orderedColumns.filter(col => 
          typeof col.accessor === 'string' && 
          priorityReadOnlyColumns.includes(col.accessor)
        ).length > 0 && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 1.5, 
              mb: 2, 
              bgcolor: 'white',
              border: '1px solid rgba(0, 0, 0, 0.12)'
            }}
            data-testid="edit-modal-priority-section"
          >
            <Grid container spacing={2}>
              {orderedColumns
                .filter(col => 
                  typeof col.accessor === 'string' && 
                  priorityReadOnlyColumns.includes(col.accessor)
                )
                .map((column) => (
                  <Grid item xs={12} sm={4} key={column.accessor as string}>
                    {renderField(column)}
                  </Grid>
                ))}
            </Grid>
          </Paper>
        )}
        
        {/* Comments Field */}
        {orderedColumns.filter(col => 
          typeof col.accessor === 'string' && 
          textAreaColumns.includes(col.accessor)
        ).length > 0 && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 1.5, 
              mb: 2,
              bgcolor: 'white',
              border: '1px solid rgba(0, 0, 0, 0.12)'
            }}
            data-testid="edit-modal-comments-section"
          >
            <Grid container>
              {orderedColumns
                .filter(col => 
                  typeof col.accessor === 'string' && 
                  textAreaColumns.includes(col.accessor)
                )
                .map((column) => (
                  <Grid item xs={12} key={column.accessor as string}>
                    {renderField(column)}
                  </Grid>
                ))}
            </Grid>
          </Paper>
        )}

        {/* All Other Fields - using a dense grid layout */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 1.5,
            bgcolor: 'white',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            flex: 1,
            overflow: 'auto'
          }}
          data-testid="edit-modal-fields-section"
        >
          <Grid container spacing={1}>
            {orderedColumns
              .filter(col => 
                typeof col.accessor === 'string' && 
                !priorityReadOnlyColumns.includes(col.accessor) && 
                !textAreaColumns.includes(col.accessor) &&
                col.accessor !== 'actions'
              )
              .map((column: Column<T>) => {
                const accessor = column.accessor as string;
                const isBarcode = accessor === 'barcode' || column.Header?.toString().toLowerCase() === 'barcode';
                
                return (
                  <Grid 
                    item 
                    xs={isBarcode ? 12 : 6} 
                    sm={isBarcode ? 12 : 4} 
                    md={isBarcode ? 4 : 2} 
                    lg={isBarcode ? 4 : 2} 
                    key={accessor}
                    data-testid={`edit-modal-grid-item-${accessor}`}
                  >
                    {renderField(column)}
                  </Grid>
                );
              })}
          </Grid>
        </Paper>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ p: 1.5, gap: '16px', borderTop: '1px solid rgba(0, 0, 0, 0.12)' }} data-testid="edit-modal-actions">
        <Button 
          onClick={handleCancel}
          variant="secondary"
          size="small"
          data-testid="edit-modal-cancel-button"
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button 
          onClick={handleSave}
          variant="primary"
          size="small"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          data-testid="edit-modal-save-button"
        >
          {t('common.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditModal; 